import { Popper } from "@mui/base/Popper";
import {
    useAutocomplete,
    type AutocompleteChangeReason,
    type AutocompleteInputChangeReason,
} from "@mui/base/useAutocomplete";
import { Box, Stack, type StackProps, useTheme } from "@mui/material";
import type { InputBaseComponentProps } from "@mui/material/InputBase";
import { FoodsIcon, FoodsIconButton, FoodsTextInput, Typography } from "@foodpilot/foods";
import { useId, useState, type MouseEvent } from "react";

import { FoodsField, FoodsLoadingIndicator } from "./FoodsFieldPrimitives";

export type FoodsAsyncComboboxFieldProps<T> = {
    label?: string;
    required?: boolean;
    helperText?: string;
    errorText?: string;
    placeholder?: string;
    value: T | string | null;
    inputValue?: string;
    options: readonly T[];
    filterOptions?: (options: readonly T[], inputValue: string) => T[];
    getOptionId: (option: T) => number | string;
    getOptionLabel: (option: T) => string;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    onInputChange: (value: string, reason: AutocompleteInputChangeReason) => void;
    onOptionChange: (value: T | null) => void;
    onFreeTextCommit?: (value: string) => void;
    disabled?: boolean;
    loading?: boolean;
    loadingText: string;
    noOptionsText: string;
    clearLabel: string;
    openLabel: string;
    closeLabel: string;
    freeSolo?: boolean;
    clearOnBlur?: boolean;
    openOnFocus?: boolean;
    selectOnFocus?: boolean;
    showPopupIndicator?: boolean;
    appearance?: "default" | "inline";
    stackProps?: StackProps;
};

/**
 * Accessible async combobox behavior with a Foods-only visual surface.
 * `@mui/base` supplies headless keyboard/ARIA semantics; no Material control is rendered.
 */
export const FoodsAsyncComboboxField = <T,>({
    label,
    required = false,
    helperText,
    errorText,
    placeholder,
    value,
    inputValue,
    options,
    filterOptions,
    getOptionId,
    getOptionLabel,
    isOptionEqualToValue,
    onInputChange,
    onOptionChange,
    onFreeTextCommit,
    disabled = false,
    loading = false,
    loadingText,
    noOptionsText,
    clearLabel,
    openLabel,
    closeLabel,
    freeSolo = true,
    clearOnBlur = false,
    openOnFocus = true,
    selectOnFocus = false,
    showPopupIndicator = true,
    appearance = "default",
    stackProps,
}: FoodsAsyncComboboxFieldProps<T>) => {
    const theme = useTheme();
    const generatedId = useId();
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
    const autocomplete = useAutocomplete<T, false, false, boolean>({
        id: `foods-async-combobox-${generatedId}`,
        options,
        value,
        ...(inputValue === undefined ? {} : { inputValue }),
        disabled,
        freeSolo,
        openOnFocus,
        clearOnBlur,
        selectOnFocus,
        filterOptions: (items, state) => filterOptions?.(items, state.inputValue) ?? items,
        getOptionKey: (option) => (typeof option === "string" ? option : getOptionId(option)),
        getOptionLabel: (option) => (typeof option === "string" ? option : getOptionLabel(option)),
        isOptionEqualToValue: (option, selectedValue) =>
            typeof selectedValue !== "string" &&
            (isOptionEqualToValue?.(option, selectedValue) ?? getOptionId(option) === getOptionId(selectedValue)),
        onInputChange: (_event, nextValue, reason) => onInputChange(nextValue, reason),
        onChange: (_event, nextValue, reason: AutocompleteChangeReason) => {
            if (reason === "clear" || nextValue === null) {
                onOptionChange(null);
                return;
            }

            if (typeof nextValue === "string") {
                if (reason === "createOption") onFreeTextCommit?.(nextValue);
                return;
            }

            onOptionChange(nextValue);
        },
    });
    const {
        getClearProps,
        getInputProps,
        getListboxProps,
        getOptionProps,
        getPopupIndicatorProps,
        getRootProps,
        groupedOptions,
        inputValue: resolvedInputValue,
        popupOpen,
        setAnchorEl,
    } = autocomplete;
    const { ref: inputRef, ...inputProps } = getInputProps();
    const clearProps = getClearProps();
    const popupIndicatorProps = getPopupIndicatorProps();
    const hasInput = resolvedInputValue.trim() !== "";
    const attachRoot = (node: HTMLElement | null) => {
        setAnchorElement(node);
        (setAnchorEl as unknown as (element: HTMLElement | null) => void)(node);
    };

    return (
        <FoodsField
            label={label}
            required={required}
            helperText={helperText}
            errorText={errorText}
            stackProps={stackProps}
        >
            <Box {...getRootProps()} ref={attachRoot} sx={{ width: "100%" }}>
                <FoodsTextInput
                    value={resolvedInputValue}
                    onChange={() => undefined}
                    disabled={disabled}
                    error={Boolean(errorText)}
                    ExtraInputProps={{
                        inputRef,
                        placeholder,
                        inputProps: {
                            ...inputProps,
                            "aria-label": label,
                        } as InputBaseComponentProps,
                        sx:
                            appearance === "inline" ?
                                {
                                    "&.MuiInputBase-root": {
                                        ...theme.typography["body-regular"],
                                        backgroundColor: "transparent",
                                        borderColor: "transparent",
                                        borderRadius: 0,
                                        borderWidth: 0,
                                        height: theme.spacing(2.8),
                                        minHeight: 0,
                                        padding: 0,
                                        "&:hover": { borderColor: "transparent" },
                                        "&.Mui-focused": {
                                            borderColor: "transparent",
                                            boxShadow: `inset 0 -${theme.custom.strokes.containerDivider.width} 0 ${theme.palette.primary[600]}`,
                                        },
                                        "& .MuiInputBase-input": {
                                            ...theme.typography["body-regular"],
                                            height: "auto",
                                            padding: 0,
                                        },
                                        "& [data-foods-combobox-actions]": {
                                            opacity: 0,
                                            transition: theme.transitions.create("opacity", {
                                                duration: theme.transitions.duration.shortest,
                                            }),
                                        },
                                        "&:hover [data-foods-combobox-actions], &.Mui-focused [data-foods-combobox-actions]":
                                            {
                                                opacity: 1,
                                            },
                                    },
                                }
                            :   undefined,
                        endAdornment: (
                            <Stack data-foods-combobox-actions direction="row" alignItems="center" gap={0.5}>
                                {loading && <FoodsLoadingIndicator label={loadingText} />}
                                {hasInput && !disabled && (
                                    <FoodsIconButton
                                        size="xs"
                                        icon="close"
                                        buttonProps={{
                                            "aria-label": clearLabel,
                                            onClick: (event) =>
                                                clearProps.onClick?.(event as MouseEvent<HTMLButtonElement>),
                                            type: "button",
                                        }}
                                    />
                                )}
                                {showPopupIndicator && (
                                    <FoodsIconButton
                                        size="xs"
                                        icon={popupOpen ? "arrowUpShort" : "arrowDownShort"}
                                        buttonProps={{
                                            "aria-label": popupOpen ? closeLabel : openLabel,
                                            disabled,
                                            onClick: (event) =>
                                                popupIndicatorProps.onClick?.(event as MouseEvent<HTMLButtonElement>),
                                            type: "button",
                                        }}
                                    />
                                )}
                            </Stack>
                        ),
                    }}
                    StackProps={{ width: "100%" }}
                />
            </Box>
            <Popper
                open={popupOpen && anchorElement !== null}
                anchorEl={anchorElement}
                placement="bottom-start"
                style={{ width: anchorElement?.clientWidth, zIndex: theme.zIndex.modal + 1 }}
            >
                <Box
                    component="ul"
                    {...getListboxProps()}
                    sx={{
                        backgroundColor: theme.custom.grey[100],
                        border: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                        borderRadius: theme.spacing(1.5),
                        boxShadow: theme.custom.shadows.grey,
                        listStyle: "none",
                        margin: theme.spacing(0.5, 0, 0),
                        maxHeight: theme.spacing(37.5),
                        overflowY: "auto",
                        padding: theme.spacing(1, 0),
                    }}
                >
                    {loading && groupedOptions.length === 0 && (
                        <Box component="li" sx={{ padding: theme.spacing(1, 2) }}>
                            <Typography variant="body-regular" sx={{ color: theme.custom.grey[1400] }}>
                                {loadingText}
                            </Typography>
                        </Box>
                    )}
                    {!loading && groupedOptions.length === 0 && (
                        <Box component="li" sx={{ padding: theme.spacing(1, 2) }}>
                            <Typography variant="body-regular" sx={{ color: theme.custom.grey[1400] }}>
                                {noOptionsText}
                            </Typography>
                        </Box>
                    )}
                    {(groupedOptions as T[]).map((option, index) => {
                        const { key, ...optionProps } = getOptionProps({ option, index });

                        return (
                            <Box
                                component="li"
                                key={key}
                                {...optionProps}
                                sx={{
                                    alignItems: "center",
                                    cursor: "pointer",
                                    display: "flex",
                                    minHeight: theme.spacing(5),
                                    padding: theme.spacing(1, 2),
                                    "&.Mui-focused": { backgroundColor: theme.custom.grey[300] },
                                    '&[aria-selected="true"]': {
                                        backgroundColor: theme.palette.primary[100],
                                        color: theme.palette.primary[700],
                                    },
                                }}
                            >
                                <Typography variant="body-regular" sx={{ color: "inherit" }}>
                                    {getOptionLabel(option)}
                                </Typography>
                                {optionProps["aria-selected"] && (
                                    <Box sx={{ display: "flex", marginLeft: "auto" }}>
                                        <FoodsIcon icon="check" size={2} />
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Popper>
        </FoodsField>
    );
};
